import { generalStatusEnum } from 'constants/global.constants';
import { PaymentStatus } from 'constants/payment.constants';
import { moneyActivityTypes } from 'constants/customer-account.constants';
import { BalanceType } from 'constants/balance.constants';

/** Must match `WebhookMonitoringStatusBucket` in `@altshuler-open/webhook-core` (server). Do not add webhook-core to this SPA — it pulls Sequelize. */
export const WebhookMonitoringStatusBucket = {
	All: 'all',
	Active: 'active',
	Completed: 'completed',
	DeadLetter: 'dead_letter',
} as const;

export type WebhookMonitoringStatusBucket =
	(typeof WebhookMonitoringStatusBucket)[keyof typeof WebhookMonitoringStatusBucket];

export const WebhookMonitoringProvider = {
	CurrencyCloud: 'currency-cloud',
	IConduct: 'iconduct',
	Hyper: 'hyper',
} as const;

export type WebhookMonitoringProvider = (typeof WebhookMonitoringProvider)[keyof typeof WebhookMonitoringProvider];

export const WebhookMonitoringOperationType = {
	Payment: moneyActivityTypes.PAYMENT,
	Conversion: moneyActivityTypes.CONVERSION,
	DigitalBalanceTopUp: BalanceType.DigitalTopping,
	BalanceRefund: moneyActivityTypes.BALANCE_REFUND,
	ConversionFee: moneyActivityTypes.CONVERSION_FEE,
	BalanceAddition: BalanceType.Topping,
	Withdrawal: BalanceType.Withdrawal,
	PaymentFee: moneyActivityTypes.PAYMENT_FEE,
	CreateCompany: 'Create Company',
	UpdateCreditCard: 'Update Credit Card',
} as const;

export type WebhookMonitoringOperationType =
	(typeof WebhookMonitoringOperationType)[keyof typeof WebhookMonitoringOperationType];

/** Matches detailType from open-backoffice-server webhook_monitoring.helper.ts */
export const WebhookMonitoringDetailType = {
	HyperCreateCompany: 'hyperCreateCompany',
	HyperCreditCard: 'hyperCreditCard',
} as const;

export type WebhookMonitoringDetailType =
	(typeof WebhookMonitoringDetailType)[keyof typeof WebhookMonitoringDetailType];

export const WebhookMonitoringBusinessStatus = {
	Approved: generalStatusEnum.APPROVED,
	Completed: generalStatusEnum.COMPLETED,
	Failed: PaymentStatus.Failed,
	Declined: generalStatusEnum.DECLINED,
	PendingCCTransfer: generalStatusEnum.PENDINGCCTRANSFER,
	Pending: generalStatusEnum.PENDING,
	Accepted: generalStatusEnum.ICONDUCT_ACCEPTED,
	DeclinedByProvider: generalStatusEnum.ICONDUCT_DECLINED,
} as const;

export type WebhookMonitoringBusinessStatus =
	(typeof WebhookMonitoringBusinessStatus)[keyof typeof WebhookMonitoringBusinessStatus];

export const WebhookMonitoringWebhookStatus = {
	Pending: 'pending',
	Published: 'published',
	Processing: 'processing',
	Failed: 'failed',
	Completed: 'completed',
	DeadLetter: 'dead_letter',
} as const;

export type WebhookMonitoringWebhookStatus =
	(typeof WebhookMonitoringWebhookStatus)[keyof typeof WebhookMonitoringWebhookStatus];

export const WebhookMonitoringReplayReason = {
	FailedProcessing: 'failed_processing',
	FailedPublish: 'failed_publish',
	DeadLetter: 'dead_letter',
	StalePending: 'stale_pending',
	StalePublished: 'stale_published',
	StaleProcessing: 'stale_processing',
} as const;

export const WebhookMonitoringFailedWebhookStatuses: ReadonlyArray<WebhookMonitoringWebhookStatus> = [
	WebhookMonitoringWebhookStatus.Failed,
	WebhookMonitoringWebhookStatus.DeadLetter,
];

/** Health dashboard dot colors keyed by webhook operation status (API `countsByStatus`). */
export const WebhookMonitoringHealthStatusHex: Record<string, string> = {
	[WebhookMonitoringWebhookStatus.Pending]: '#F26F21',
	[WebhookMonitoringWebhookStatus.Published]: '#F26F21',
	[WebhookMonitoringWebhookStatus.Processing]: '#F26F21',
	[WebhookMonitoringWebhookStatus.Failed]: '#FF0000',
	[WebhookMonitoringWebhookStatus.Completed]: '#008a69',
	[WebhookMonitoringWebhookStatus.DeadLetter]: '#FF0000',
};

export const WebhookMonitoringBusinessStatusesByProvider: Record<
	WebhookMonitoringProvider,
	WebhookMonitoringBusinessStatus[]
> = {
	[WebhookMonitoringProvider.CurrencyCloud]: [
		WebhookMonitoringBusinessStatus.Approved,
		WebhookMonitoringBusinessStatus.Completed,
		WebhookMonitoringBusinessStatus.Failed,
		WebhookMonitoringBusinessStatus.Declined,
		WebhookMonitoringBusinessStatus.PendingCCTransfer,
		WebhookMonitoringBusinessStatus.Pending,
	],
	[WebhookMonitoringProvider.IConduct]: [
		WebhookMonitoringBusinessStatus.Accepted,
		WebhookMonitoringBusinessStatus.DeclinedByProvider,
	],
	[WebhookMonitoringProvider.Hyper]: [
		WebhookMonitoringBusinessStatus.Completed,
		WebhookMonitoringBusinessStatus.Failed,
		WebhookMonitoringBusinessStatus.Pending,
	],
};

export const WebhookMonitoringOperationTypesByProvider: Record<
	WebhookMonitoringProvider,
	WebhookMonitoringOperationType[]
> = {
	[WebhookMonitoringProvider.CurrencyCloud]: [
		WebhookMonitoringOperationType.Payment,
		WebhookMonitoringOperationType.Conversion,
		WebhookMonitoringOperationType.BalanceRefund,
		WebhookMonitoringOperationType.ConversionFee,
		WebhookMonitoringOperationType.BalanceAddition,
		WebhookMonitoringOperationType.Withdrawal,
		WebhookMonitoringOperationType.PaymentFee,
	],
	[WebhookMonitoringProvider.IConduct]: [WebhookMonitoringOperationType.DigitalBalanceTopUp],
	[WebhookMonitoringProvider.Hyper]: [
		WebhookMonitoringOperationType.CreateCompany,
		WebhookMonitoringOperationType.UpdateCreditCard,
	],
};
